import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import ScheduleBadge, { formatScheduleDate } from "../components/ScheduleBadge";
import AdminRemovePlayerButton from "../components/AdminRemovePlayerButton";
import { Trophy } from "lucide-react";

function FixturesView({ leagueId }) {
  const { t } = useT();
  const { user } = useAuth();
  const viewerId = user && user !== false ? user.id : null;
  const myLeagueId = user && user !== false ? user.league_id : null;
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!leagueId) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/leagues/${leagueId}/fixtures`);
        if (!alive) return;
        setFixtures(data);
        const dates = [...new Set(data.map((m) => m.scheduled_at).filter(Boolean))].sort();
        if (dates.length) {
          // Prefer today if scheduled, otherwise the next upcoming date, otherwise the first
          const todayIso = new Date().toISOString().slice(0, 10);
          const next = dates.find((d) => d >= todayIso) || dates[0];
          setSelectedDate(next);
        }
      } catch (e) { /* noop */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [leagueId]);

  if (!leagueId) return <Panel className="font-mono text-[#71717A] text-sm">{t("fx.noLock")}</Panel>;
  if (loading) return <TactLoader label={t("fx.loading")} />;
  if (fixtures.length === 0) return <Panel className="font-mono text-[#71717A] text-sm">{t("fx.empty")}</Panel>;

  // Day-based calendar: every distinct scheduled_at becomes a day button.
  const dates = [...new Set(fixtures.map((m) => m.scheduled_at).filter(Boolean))].sort();

  // Fallback: fixtures exist but NONE have scheduled_at (old data pre-migration).
  // Render a round-based grid so the page is never empty. Backend heal will
  // populate dates on the next request.
  if (dates.length === 0) {
    const rounds = [...new Set(fixtures.map((m) => m.round))].sort((a, b) => a - b);
    const byRound = {};
    fixtures.forEach((m) => { (byRound[m.round] ||= []).push(m); });
    return (
      <div className="space-y-6" data-testid="fixtures-view">
        <Panel className="font-mono text-[#71717A] text-xs" testId="fixtures-migrating">
          [ MIGRATING SCHEDULE… REFRESH IN A MOMENT TO SEE CALENDAR DATES ]
        </Panel>
        {rounds.map((r) => (
          <div key={r}>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-3">// ROUND {r}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(byRound[r] || []).map((m) => (
                <div key={m.id} className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-4 flex items-center justify-between gap-2" data-testid={`fixture-${m.id}`}>
                  <span className={`font-heading text-lg truncate flex-1 ${viewerId === m.home_id ? "text-[#34C759]" : ""}`}>{m.home?.username || "—"}</span>
                  <span className="font-mono text-xs text-[#71717A] px-2">VS</span>
                  <span className={`font-heading text-lg truncate flex-1 text-right ${viewerId === m.away_id ? "text-[#34C759]" : ""}`}>{m.away?.username || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  const matchesByDate = {};
  fixtures.forEach((m) => { (matchesByDate[m.scheduled_at] ||= []).push(m); });
  // Pair up Game 1 + Game 2 by the unordered (home_id, away_id) pair, show as a single card per "game day"
  const activeMatches = matchesByDate[selectedDate] || [];
  const pairs = {};
  activeMatches.forEach((m) => {
    const key = [m.home_id, m.away_id].sort().join("|");
    (pairs[key] ||= []).push(m);
  });
  const pairList = Object.values(pairs).sort((a, b) => a[0].round - b[0].round);

  return (
    <div className="space-y-6" data-testid="fixtures-view">
      {myLeagueId && (
        <div className="flex justify-end" data-testid="fixtures-view-header-actions">
          <Link
            to={`/leagues/${myLeagueId}`}
            data-testid="fixtures-my-league-btn"
            className="inline-flex items-center gap-2 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors"
          >
            <Trophy className="w-3 h-3" />
            {t("fx.viewMyLeague")}
          </Link>
        </div>
      )}
      <div
        className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 snap-x snap-mandatory"
        data-testid="round-selector"
      >
        {dates.map((d) => {
          const dayMs = matchesByDate[d] || [];
          const pairCount = new Set(dayMs.map((m) => [m.home_id, m.away_id].sort().join("|"))).size;
          const allDone = dayMs.every((m) => m.status === "confirmed" || m.admin_override);
          const youPlay = viewerId && dayMs.some((m) => m.home_id === viewerId || m.away_id === viewerId);
          const isSelected = d === selectedDate;
          const todayIso = new Date().toISOString().slice(0, 10);
          const isToday = d === todayIso;
          const stateClass = isSelected
            ? "border-[#7C3AED] text-[#7C3AED] bg-[#7C3AED]/10 shadow-[0_0_18px_rgba(124,58,237,0.35)]"
            : youPlay
              ? "border-[#7C3AED]/70 text-[#09090B] dark:text-[#F4F4F5] hover:border-[#7C3AED] shadow-[0_0_14px_rgba(124,58,237,0.25)]"
              : allDone
                ? "border-[#00FF87]/40 text-[#059669] dark:text-[#00FF87] hover:border-[#00FF87]"
                : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#09090B] dark:text-[#F4F4F5] hover:border-[#7C3AED]";
          return (
            <button
              key={d}
              onClick={() => setSelectedDate(d)}
              data-testid={`round-btn-${d}`}
              className={`snap-start min-w-[110px] flex-shrink-0 px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] border transition-all duration-200 text-left leading-tight relative ${stateClass}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[9px] tracking-[0.3em] text-[#71717A]">
                  {formatScheduleDate(d).split(",")[0].toUpperCase()}
                </span>
                {isToday && <span className="today-dot" data-testid={`today-dot-${d}`} aria-label="today" />}
              </div>
              <div className="mt-0.5">{formatScheduleDate(d).split(",")[1].trim().toUpperCase()}</div>
              <div className="text-[8px] tracking-[0.25em] text-[#A1A1AA] mt-0.5">
                {pairCount} PAIRS{youPlay ? " · YOU" : ""}
              </div>
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <div className="flex items-baseline justify-between flex-wrap gap-3" data-testid="active-round-date">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">
            // MATCHDAY · {formatScheduleDate(selectedDate).toUpperCase()}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
            {pairList.length} PAIRS · {activeMatches.length} MATCHES · <span className="text-[#7C3AED]">1 GAME = 2 LEGS (HOME + REVERSE) SAME DAY</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pairList.map((legs) => (
          <PairCard key={legs[0].id} legs={legs} viewerId={viewerId} />
        ))}
      </div>
    </div>
  );
}

function PairCard({ legs, viewerId }) {
  // Order: Game 1 first (order=1), Game 2 second (order=2)
  const ordered = [...legs].sort((a, b) => (a.order || 1) - (b.order || 1));
  const game1 = ordered[0];
  const game2 = ordered[1];
  const homeIsMe = viewerId && (game1.home_id === viewerId || game1.away_id === viewerId);
  // Build canonical home/away display = whoever was home in game1
  const homeName = game1.home?.username || "—";
  const awayName = game1.away?.username || "—";
  const homeGid = game1.home?.game_id;
  const awayGid = game1.away?.game_id;
  const homeIsViewer = viewerId === game1.home_id;
  const awayIsViewer = viewerId === game1.away_id;
  // Linking: if viewer is in this pair, link to H2H; otherwise link to game 1's match detail
  const target = viewerId === game1.home_id
    ? `/h2h/${game1.away_id}`
    : viewerId === game1.away_id
      ? `/h2h/${game1.home_id}`
      : `/matches/${game1.id}`;

  const allDone = ordered.every((m) => m.status === "confirmed" || m.admin_override);
  const anyDisputed = ordered.some((m) => m.status === "disputed");
  const cardBorder = allDone
    ? "border-[#34C759]/50 hover:border-[#34C759]"
    : anyDisputed
      ? "border-[#E11D48]/60 hover:border-[#E11D48]"
      : "border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#7C3AED]";

  return (
    <Link to={target} className={`block bg-[#FFFFFF] dark:bg-[#1A1A1E] border transition-colors ${cardBorder}`} data-testid={`fixture-${game1.id}`}>
      <div className="flex items-center justify-between px-5 pt-4 gap-2 flex-wrap">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">
          RD {game1.round}<span className="text-[#7C3AED]"> · {formatScheduleDate(game1.scheduled_at).toUpperCase()}</span>
        </span>
        <ScheduleBadge match={game1} />
      </div>

      <div className="px-5 py-4 flex items-center justify-between gap-2">
        <div className={`font-heading text-xl uppercase truncate flex-1 ${homeIsViewer ? "text-[#34C759]" : ""}`}>
          <span className="inline-flex items-center gap-1.5">
            {homeName}
            <AdminRemovePlayerButton
              userId={game1.home_id}
              username={homeName}
              targetRole={game1.home?.role || "player"}
              size="xs"
              testId={`fixture-remove-home-${game1.id}`}
            />
          </span>
          <div className="font-mono text-[10px] text-[#71717A] truncate normal-case">{homeGid}</div>
        </div>
        <div className="font-mono text-sm text-[#71717A] px-2">VS</div>
        <div className={`font-heading text-xl uppercase truncate flex-1 text-right ${awayIsViewer ? "text-[#34C759]" : ""}`}>
          <span className="inline-flex items-center gap-1.5 justify-end flex-row-reverse">
            {awayName}
            <AdminRemovePlayerButton
              userId={game1.away_id}
              username={awayName}
              targetRole={game1.away?.role || "player"}
              size="xs"
              testId={`fixture-remove-away-${game1.id}`}
            />
          </span>
          <div className="font-mono text-[10px] text-[#71717A] truncate normal-case">{awayGid}</div>
        </div>
      </div>

      <div className="border-t border-[#E4E4E7] dark:border-[#2A2A30] grid grid-cols-2 divide-x divide-[#E4E4E7] dark:divide-[#2A2A30]">
        <GameRow label="GAME 1" sub="HOME LEG" leg={game1} />
        <GameRow label="GAME 2" sub="REVERSE LEG" leg={game2} pendingGate={!(game1.status === "confirmed" || game1.admin_override)} />
      </div>
    </Link>
  );
}

function GameRow({ label, sub, leg, pendingGate = false }) {
  const finalized = leg?.status === "confirmed" || leg?.admin_override;
  const disputed = leg?.status === "disputed";
  const locked = pendingGate && !finalized;
  const labelBg = label === "GAME 1"
    ? "bg-[#7C3AED] text-white"
    : finalized ? "bg-[#34C759] text-black" : locked ? "bg-[#E11D48]/80 text-[#09090B] dark:text-[#F4F4F5]" : "bg-[#EA580C] text-white";
  const score = finalized ? `${leg.confirmed_score_home}  :  ${leg.confirmed_score_away}` : "— : —";
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className={`font-heading text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${labelBg}`}>{label}</span>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#71717A]">{sub}</span>
      </div>
      <div className="font-mono text-sm flex items-center gap-2">
        {locked && <span className="font-mono text-[9px] text-[#E11D48] uppercase tracking-[0.2em]">LOCKED</span>}
        <span className={finalized ? "text-[#34C759]" : disputed ? "text-[#E11D48]" : "text-[#71717A]"}>{score}</span>
      </div>
    </div>
  );
}

function MatchRow({ m, viewerId }) {
  const { t } = useT();
  const status = m.status;
  const finalized = status === "confirmed" || m.admin_override;
  const statusColor =
    status === "confirmed" ? "text-[#34C759]" : status === "disputed" ? "text-[#E11D48]" : "text-[#71717A]";
  const score = status === "confirmed"
    ? `${m.confirmed_score_home} - ${m.confirmed_score_away}`
    : "— : —";
  // If viewer is in this fixture, link to head-to-head with their opponent
  let target = `/matches/${m.id}`;
  if (viewerId) {
    if (m.home_id === viewerId) target = `/h2h/${m.away_id}`;
    else if (m.away_id === viewerId) target = `/h2h/${m.home_id}`;
  }
  const homeIsMe = viewerId && m.home_id === viewerId;
  const awayIsMe = viewerId && m.away_id === viewerId;
  const cardBorder = finalized ? "border-[#34C759]/40 hover:border-[#34C759]" : "border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#7C3AED]";
  return (
    <Link to={target} className={`block bg-[#FFFFFF] dark:bg-[#1A1A1E] border transition-colors ${cardBorder}`} data-testid={`fixture-${m.id}`}>
      <div className="flex items-center justify-between px-5 pt-4 gap-2 flex-wrap">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">
          ROUND {m.round}{m.scheduled_at ? <span className="text-[#7C3AED]"> · {formatScheduleDate(m.scheduled_at).toUpperCase()}</span> : null}
        </span>
        <div className="flex items-center gap-2">
          <ScheduleBadge match={m} />
          <span className={`font-mono text-[10px] uppercase tracking-[0.3em] ${statusColor}`}>
            {finalized ? "COMPLETED" : status}{m.auto_drawn ? " · AUTO" : ""}
          </span>
        </div>
      </div>
      <div className="px-5 py-5 flex items-center justify-between">
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("fx.home")}</div>
          <div className={`font-heading text-xl uppercase truncate ${homeIsMe ? "text-[#34C759]" : ""}`}>{m.home?.username || "—"}</div>
          <div className="font-mono text-[10px] text-[#71717A] truncate">{m.home?.game_id}</div>
        </div>
        <div className={`px-4 font-mono text-xl ${finalized ? "text-[#34C759]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>{score}</div>
        <div className="flex-1 text-right">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("fx.away")}</div>
          <div className={`font-heading text-xl uppercase truncate ${awayIsMe ? "text-[#34C759]" : ""}`}>{m.away?.username || "—"}</div>
          <div className="font-mono text-[10px] text-[#71717A] truncate">{m.away?.game_id}</div>
        </div>
      </div>
    </Link>
  );
}

export default function Fixtures({ activeLeague }) {
  const { t } = useT();
  const { user } = useAuth();
  const myLeagueId = user && user !== false ? user.league_id : null;
  const isOpen = activeLeague?.status === "open";
  return (
    <div data-testid="fixtures-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SectionTitle kicker={activeLeague ? `// ${activeLeague.name}` : "//"} testId="fixtures-title">{t("fx.title")}</SectionTitle>
        {myLeagueId && (
          <Link
            to={`/leagues/${myLeagueId}`}
            data-testid="fixtures-my-league-btn"
            className="inline-flex items-center gap-2 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors"
          >
            <Trophy className="w-3 h-3" />
            {t("fx.viewMyLeague")}
          </Link>
        )}
      </div>
      {isOpen
        ? <SchedulePreview activeLeague={activeLeague} />
        : <FixturesView leagueId={activeLeague?.id} />}
    </div>
  );
}

/**
 * Read-only preview of the calendar shown BEFORE the league has 20 players
 * locked in. Renders all 38 days of the schedule (the same date range the
 * live fixtures will use the moment the league locks) so users can see what
 * "1 game = 2 legs same day" will look like in advance.
 */
function SchedulePreview({ activeLeague }) {
  // Anchor matches the backend `_schedule_base` logic: the league starts the
  // calendar day AFTER it locks (base_date = today + 1 UTC). Preview assumes
  // "lock happens today" so the first matchday is tomorrow.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const base = new Date(today);
  base.setDate(base.getDate() + 1);
  const days = Array.from({ length: 38 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    return d.toISOString().slice(0, 10);
  });
  const slots = activeLeague ? activeLeague.capacity - activeLeague.player_count : 20;
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-6" data-testid="fixtures-preview">
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#EA580C] p-5" data-testid="preview-banner">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C] mb-2">// schedule preview</div>
        <div className="font-heading text-xl uppercase tracking-wider">
          {slots > 0 ? `${slots} SLOTS REMAINING — schedule locks when ${activeLeague?.capacity || 20}/20 register` : "League is full — fixtures generating…"}
        </div>
        <div className="font-mono text-[11px] text-[#71717A] mt-2 leading-relaxed">
          The calendar below is the EXACT date range your fixtures will run across once the league fills to 20 players —
          <span className="text-[#09090B] dark:text-[#F4F4F5]"> {formatScheduleDate(days[0]).toUpperCase()}</span> through
          <span className="text-[#09090B] dark:text-[#F4F4F5]"> {formatScheduleDate(days[days.length - 1]).toUpperCase()}</span>.
          Each day you'll see <span className="text-[#7C3AED]">5 pairs · 10 matches</span>, with{" "}
          <span className="text-[#7C3AED]">1 game = 2 legs (HOME + REVERSE)</span> on the same day.
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 snap-x" data-testid="round-selector">
        {days.map((d) => {
          const isToday = d === todayIso;
          return (
            <div
              key={d}
              data-testid={`round-btn-${d}`}
              className="snap-start min-w-[110px] flex-shrink-0 px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] text-left leading-tight cursor-default relative"
            >
              <div className="flex items-center justify-between">
                <span className="text-[9px] tracking-[0.3em] text-[#A1A1AA]">
                  {formatScheduleDate(d).split(",")[0].toUpperCase()}
                </span>
                {isToday && <span className="today-dot" aria-label="today" />}
              </div>
              <div>{formatScheduleDate(d).split(",")[1].trim().toUpperCase()}</div>
              <div className="text-[8px] tracking-[0.25em] text-[#D4D4D8] mt-0.5">5 PAIRS</div>
            </div>
          );
        })}
      </div>

      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-dashed border-[#E4E4E7] dark:border-[#2A2A30] p-8 text-center" data-testid="preview-locked">
        <div className="font-heading text-2xl uppercase tracking-wider text-[#71717A]">Fixtures unlock at 20/20</div>
        <div className="font-mono text-[11px] text-[#A1A1AA] mt-2 tracking-[0.2em]">
          PLAYER PAIRINGS + GAME 1 / GAME 2 LABELS APPEAR HERE THE MOMENT THE 20TH PLAYER REGISTERS
        </div>
      </div>
    </div>
  );
}

export { FixturesView, MatchRow, SchedulePreview };
